/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import invert from 'lodash.invert';
import isArray from 'lodash.isarray';
import isNil from 'lodash.isnil';
import isNumber from 'lodash.isnumber';
import isObject from 'lodash.isobject';
import isString from 'lodash.isstring';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {
  AccountErrorMessageIndex,
  Entity,
  EntityStatus,
  GenericObject,
  HVSRecord
} from '@ion/core/src/types/entities';
import {
  ServiceError
} from '@ion/core/src/types/services';
import {getEventSpeed} from '@ion/core/src/units/helpers';
import {
  dateFormatter,
  DateType,
  isSame,
  startOfDay,
  timeDiff,
  timeFormatter
} from '@ion/core/src/utils/DateUtil';
import {
  safeJSONParse
} from '@ion/core/src/utils/JSONUtils';
import {
  clientGridFilterDateComparator,
  dateTimeSortComparator
} from '@ion/core/src/utils/SortUtils';
import {
  capitalizeFirstLetter,
  contains,
  isEmpty
} from '@ion/core/src/utils/StringUtil';
import {AddressType} from '@ion/dls/src/maps/components/Address';
import {ViewSubGroupsPosition} from '../types/types';

export type ResolveValue = string | number | boolean;
export type ResolveOptions = {
  resolver?: string,
  transformer?: Function
};
interface GetContainerHeightParams {
  isCentered: boolean;
  isFullPageDisplay: boolean;
};

export const FILE_TYPES = '.csv, .xls, .xlsx';

export const ADMIN_GROUPS_PATH      = 'admin-groups:toggleUserPrompt';
export const ACCOUNT_ERR_MSG_PATH   = 'admin-groups:dialog.errors.cannotAssignedToAccount';
export const ACCOUNT_EDIT_ERR_MSG   = 'admin-groups:dialog.errors.noSsoAccountEditPermission';

/**
 * This utility function helps to translate message with the given path. Translate to close if isReadOnlyUser is true.
 *
 *  ```typescript
 *
 *  const msg = translatePromptAction('notifications:warning.actionLabel', true); // common:close
 *  ```
 */
export const translatePromptAction = (
  path: string,
  isReadOnlyUser = false,
  msgOfReadOnlyUser = 'common:close'
) => {
  if (isReadOnlyUser) {
    return translate(msgOfReadOnlyUser);
  }
  return translate(path);
};

/**
 * This utility function helps to calculate container height.
 * Delicated to ContentLoader.tsx
 *
 *  ```typescript
 *
 *  const containerHeight = getContainerHeight({isCentered: isContainerCentered, isFullPageDisplay: fullPage});
 *  ```
 */
export const getContainerHeight = ({isCentered, isFullPageDisplay}: GetContainerHeightParams) => {
  let height = 'calc(100vh - 64px)';
  if (!isCentered) {
    height = isFullPageDisplay ? '100vh' : 'auto';
  };
  return height;
};

// [DLS TODO]: ADD RESOLVE CORRESPONDING VALUE UNIT TESTS ...
/**
 * @resolveValue
 *
 * Provides a utility which resolves a non-nil + empty string value and returns
 * the result. This function provides a convenience for components which will
 * render an empty string, null, or undefined value.
 *
 * ```typescript
 *
 *   import {translate} from '@ion/core/src/locale/locale';
 *   import {resolveValue} from '@ion/dls/src/utils/ComponentUtils';
 *   import {calendarDate} from '@ion/core/src/utils/DateUtil';
 *
 *   const resolved = resolveValue('');
 *   // 'N/A'
 *
 *   const resolved = resolveValue(null, {resolver: translate('common:unavailable')});
 *   // 'Unavailable'
 *
 *   const resolved = resolveValue(new Date('...'), {transformer: calendarDate});
 *   // '09/25/20'
 *
 * ```
 *
 */
export const resolveValue = (value: ResolveValue, options?: ResolveOptions): string => {
  if (isNil(value) || value === '') {
    return options?.resolver || translate('common:notAvailable');
  }
  return options?.transformer?.(value) || value as string;
};

/**
 * @partialStatusMessageFormatter
 *
 * Util which helps in formatting the partial success/failure message to be displayed ...
 *
 * ```typescript
 *
 *   import {partialStatusMessageFormatter} from '@ion/dls/src/utils/';
 *
 *   const result = partialStatusMessageFormatter([1,2,3,4], [1,2,3,4,5,6]);
 *   // '4/6'
 *
 *   const result = partialStatusMessageFormatter([], [1,2,3,4,5,6]);
 *   // 'No'
 *
 *   const result = partialStatusMessageFormatter([1,2,3,4], [1,2,3,4,5], 'in');
 *   // '4 in 5'
 *
 * ```
 *
 */
export const partialStatusMessageFormatter = (
  results: Array<any>,
  total: Array<any>,
  separator: string = '/'
): string => {
  const resultsCount = results?.length;
  const totalCount   = total?.length;
  if (resultsCount >= 0 && totalCount >= 0) {
    const errorsCount = totalCount - resultsCount;
    const hasNoErrors = errorsCount === 0;
    const partialStatusMessage = resultsCount === 0
      ? translate('common:editors.inheritable.no') : `${resultsCount}${separator}${totalCount}`;
    return hasNoErrors ? `${resultsCount}` : partialStatusMessage;
  }
  return '';
};

export const formatErrorMessage = ({errorText, message}: ServiceError) => {
  if (!isNil(errorText)) {
    if (isObject(errorText)) {
      const {error} = errorText;
      return error?.description || translate('notifications:error.client');
    } else if (errorText?.indexOf('-') > -1) {
      if (/(?<=\[[^\]]*)-(?=[^\]]*\])/g.test(errorText)) {
        return errorText;
      }
      const result = errorText.split('-');
      if (result.length > 2 && isEmpty(result[0].replaceAll('\'', ''))) {
        result.shift();
        return result.join();
      }
      if (result.length === 2 && isEmpty(result[1])) {
        return result[0] as string;
      }
      if (result.length === 4 && isEmpty(result[3])) {
        return result?.map(item => item)?.join(' ') as string;
      }
      return result[1] as string;
    } else if (errorText?.indexOf(':') > -2) {
      const result = errorText.split(':');
      return result?.map(item => item)?.join(' ') as string;
    } else {
      return errorText;
    }
  }
  return message || translate('notifications:error.client');
};

/**
 * The `getErrorMessage` is a utility function designed to extract a meaningful error message
 * from a service response object. It checks for specific error information in a prioritized
 * order and returns the most relevant error message available.
 *
 * ```typescript
 * 
 * import {getErrorMessage} from '@ion/dls/src/utils/ComponentUtils';
 * 
 * const error = {
 *   response: {
 *     data: {
 *       error: 'Internal server error'
 *     }
 *   }
 * };
 * 
 * const errorMessage = getErrorMessage(error);
 * console.log(errorMessage); // Output: 'Internal server error'
 * 
 * ```
 *
 * @param error The error message from the axioms client.
 * 
 * @returns - Returns the error message from the response, or a fallback message
 *                     if no specific error information is available.
 */
export const getErrorMessage = (error: GenericObject) => {
  const {errorText, message, response} = error || {};
  return response?.data?.error ?? errorText ?? message;
};

/**
 * This function returns all account specific errors ...
 */
export const formatAccountErrorMessage = (errorText: string | {error: {message: string}}, parsed): string => {
  if (!isNil(errorText)) {
    const entity     = Entity.account;
    let errorMessage = errorText;
    if (isObject(errorText)) {
      errorMessage = errorText?.error?.message || '';
    }
    let msg = errorMessage as string;
    if (contains(msg, 'solomon')) {
      return translate(`${ADMIN_GROUPS_PATH}.exists`);
    } else if (contains(msg, translate(ACCOUNT_ERR_MSG_PATH))) {
      const parentName = parsed?.account?.name || '';
      let errMsg       = translate(`${ADMIN_GROUPS_PATH}.cannotAssignedToAccountText`, {parentName});
      if (msg.indexOf(':') > -1) {
        const message = msg.split(':');
        if (!isEmpty(message?.[0])) {
          errMsg = `${message[0]} '${parentName}'`;
        }
      };
      msg = errMsg;
      return msg;
    } else if (contains(msg, translate(ACCOUNT_EDIT_ERR_MSG))) {
      const currentUser = msg?.match(/\[([^\]]+)\]/)?.[1] || '';
      return translate('admin-groups:dialog.errors.ssoAccountEditError', {accountName: parsed?.account?.name || '', userName: currentUser});
    }
    return `${entity.charAt(0).toUpperCase()}${entity.slice(1)}  ${msg?.substring(AccountErrorMessageIndex)}`;
  }
  return translate('notifications:error.client');
};

export const formatEntityErrorMessage = (entity, error: ServiceError) => {
  let result;
  const {errorText, data = '{}'} = error;
  const parsedData               = safeJSONParse(data);
  if (!isNil(errorText)) {
    if (entity === Entity.account) {
      return formatAccountErrorMessage(errorText, parsedData);
    } else if (entity === Entity.notification) {
      if (contains(errorText, 'permission for alertActionTemplate', false)) {
        return translate('admin-notifications:toggleUserPrompt.templatePermissionError');
      } else if (contains(errorText, 'A Alert with given name', false)) {
        const entityName = errorText.substring(28, errorText.indexOf('and'));
        result = entityName.concat(translate('admin-notifications:toggleUserPrompt.exists'));
      } else {
        result = errorText;
      }
    } else if (entity === Entity.notificationType) {
      result = formatErrorMessage(error);
    }
    return `${entity.charAt(0).toUpperCase()}${entity.slice(1)}  ${result}`;
  }
};

/**
 * @formatSpeed
 *
 * Util to help format speed information.
 *
 * ```typescript
 *
 *   import {formatSpeed} from '@ion/dls/src/utils/';
 *
 *   const s = formatSpeed(35.5, 'mph');
 *   // '35.5mph'
 *
 *   const s = formatSpeed(35.5, 'mph', {round: true});
 *   // '36mph'
 *
 *   const s = formatSpeed(35.5, 'mph', {truncate: true});
 *   // '35mph'
 *
 *   const s = formatSpeed(35, 'mph', {delimiter: ' '});
 *   // '35 mph'
 *
 *   const s = formatSpeed(null, null, {delimiter: ' ', placeholder: '-'});
 *   // '- -'
 *
 *   const s = formatSpeed(null, null);
 *   // ''
 *
 * ```
 *
 */
export interface FormatSpeedOptions {
  placeholder?: string;
  delimiter?: string;
  round?: boolean;
  truncate?: boolean;
  displayZero?: boolean;
};

export const formatSpeed = (
  speed: number,
  units: string,
  options?: FormatSpeedOptions
): string => {
  const {
    placeholder = '',
    delimiter = '',
    truncate = false,
    displayZero = false
  } = options || {};
  const showSpeed   = speed || (isNumber(speed) && displayZero);
  const unitsFmt    = (showSpeed && units) ? units : placeholder;
  let speedFmt;
  if (options?.round) {
    speedFmt = (showSpeed && units) ? Math.round(speed) : placeholder;
  } else if (truncate) {
    speedFmt = (showSpeed && units) ? Math.trunc(speed) : placeholder;
  } else {
    speedFmt = (showSpeed && units) ? speed : placeholder;
  }
  return `${speedFmt}${delimiter}${unitsFmt}`;
};

/**
 * Returns the greeting message based on current/custom time ...
 *
 * ```typescript
 *
 * import {getGreetingMessage} from '@ion/dls/src/utils/ComponentUtils';
 * const message = getGreetingMessage('Asif');
 *
 * ```
 * @returns the greeting message based on current/custom time
 */
export const getGreetingMessage = (text: string, hour?: number, minute?: number): string => {
  let message;
  const currentHour   = hour ?? new Date().getHours();
  const currentMinute = minute ?? new Date().getMinutes();
  if (currentHour >= 12 && (currentHour <= 17 && currentMinute <= 59)) {
    message = translate('dashboard:welcomeMessage.goodAfternoon');
  } else if (currentHour >= 18 || (currentHour <= 4 && currentMinute <= 59)) {
    message = translate('dashboard:welcomeMessage.goodEvening');
  } else {
    message = translate('dashboard:welcomeMessage.goodMorning');
  }
  return `${message}${text ? `, ${text}` : ''}`;
};

/**
 * Utility that gets the speed values (current speed and over speed limit values)
 * from a passed HVS Record.
 *
 * ```typescript
 *   import {getSpeedVals} from '@ion/dls/src/utils/';
 *
 *   getSpeedVals({
 *     speed : 34,
 *     systemOfUnits : 'US'
 *   })
 *
 *   // {speedVal: '34 mph', speedingVal: '5 mph'}
 *
 *   getSpeedVals({
 *     speed : 75,
 *     systemOfUnits : 'Metric'
 *   })
 *
 *   // {speedVal: '75 kph', speedingVal: '15 kph'}
 *
 *   getSpeedVals({
 *     speed : null,
 *     systemOfUnits : 'US'
 *   })
 *
 *   // {speedVal: '--', speedingVal: '--'}
 *
 * ```
 */
export const getSpeedVals = (record: HVSRecord): {
  speedVal: string,
  speedingVal: string
} => {
  const {speed, overSpeed, units} = getEventSpeed(record);
  const speedVal    = formatSpeed(speed, units, {delimiter: ' ', placeholder: '-'});
  const speedingVal = formatSpeed(overSpeed, units, {truncate: true, delimiter: ' ', placeholder: '-'});
  return {speedVal, speedingVal};
};

/**
 * Provides a utility to call a function with said value then call
 * same function again to reset value after delay.
 *
 * ```typescript
 *   import {resetValue} from '@ion/dls/src/utils/ComponentUtils';
 *   import { getLast7Days } from '../../../core/src/utils/DateUtil';
 *
 *   resetValue({
 *     fnCall       : setRefreshValue,
 *     initialValue : true,
 *     resetValue   : false,
 *     delay        : 750
 *   })
 *
 *   // calls `setRefreshValue(true)`
 *   // 750ms later calls `setRefreshValue(false)`
 *
 * ```
 */
export const resetValue = ({
  fnCall,
  initialValue,
  resetValue,
  delay = 500
}) => {
  fnCall(initialValue);
  const timeout = setTimeout(() => {
    fnCall(resetValue);
    clearTimeout(timeout);
  }, delay);
};

/**
 * @function translateStatus
 *
 * Convert entity translated label to status key
 *
 */
export function translateStatus(value: string): string {
  const statuses = translate('common:entityStatus', {returnObjects: true});
  const status = value ? statuses[value] : false;
  return status;
}

/**
 * @function convertStatus
 *
 * Convert entity status key to translated label
 *
 */
export function convertStatus(value: string): string {
  const statuses = invert(translate('common:entityStatus', {returnObjects: true}));
  return (capitalizeFirstLetter(statuses[value]) || EntityStatus.Enabled);
}

/**
 * The utility helps to translate the time format value to label.
 *
 * ```typescript
 *
 *  import {translateTimeFormat} from '@ion/dls/src/utils/';
 *
 *  const timeFormat1 = translateTimeFormat('hh:mm A'); // AM/PM
 *  const timeFormat2 = translateTimeFormat('hh:mm'); // 24 Hour
 *  const timeFormat3 = translateTimeFormat('hh:mm:ss A'); // AM/PM
 *
 * ```
 */
export const translateTimeFormat = (format: string, returnDefault = true): string | undefined => {
  const timeFormatOptions = translate('common:timeFormat.options', {returnObjects: true});
  if (isArray(timeFormatOptions)) {
    const matched = timeFormatOptions.find(({value}) => format === value) ||
   (returnDefault && timeFormatOptions[0]);
    return matched?.label;
  }
  return format ?? '';
};

/**
 * The utility helps to formatting the date in user prefered date & time format.
 *
 * ```typescript
 *
 *  import {dateTimeFormatter} from '@ion/dls/src/utils/ComponentUtils';
 *
 *  const dateUI1 = dateTimeFormatter('2021-04-01T16:01:31.803Z'); // 04/01/2021 09:31 PM
 *  const dateUI2 = dateTimeFormatter('2021-04-01T16:01:31.803Z', true); // 04/01/2021 09:31:31 PM
 *
 * ```
 */
export const dateTimeFormatter = (date: Date | string | DateType, includeSeconds = false): string | DateType => {
  if (date) {
    const parsed     = isString(date) && !(date as string).endsWith('Z') ? `${date}Z` : date;
    const dateFormat = UserPreferences.getSystemDateFormat();
    let timeFormat   = UserPreferences.getSystemTimeFormat();
    if (includeSeconds) {
      timeFormat = timeFormat.replace('mm', 'mm:ss');
    }
    const dateTimeFormat = `${dateFormat}, ${timeFormat}`;
    return dateFormatter(parsed, dateTimeFormat);
  }
  return date;
};

/**
 * The utility helps to formatting the date in user prefered date & time locale specific format.
 *
 * ```typescript
 *
 *  import {getFormattedDate} from '@ion/dls/src/utils/ComponentUtils';
 *
 *  const formattedDate = getFormattedDate('2021-04-01T16:01:31.803Z'); // 04/01/2021 At 09:31 PM
 *
 * ```
 */
export const getFormattedDate = (time: Date | DateType | number, includeSeconds = false): string => {
  const now            = new Date();
  const beginningOfDay = startOfDay(now);
  const isSameDay      = isSame(now, time, 'day');
  const diffDays       = Math.ceil(timeDiff(beginningOfDay, time, 'days'));
  const calendar       = dateFormatter(time, UserPreferences.getSystemDateFormat());
  const hourTime       = userPreferredTimeFormatter(time, includeSeconds);
  if (isSameDay) {
    return translate('common:time.todayAt', {time: hourTime});
  } else if (diffDays === 1) {
    return translate('common:time.yesterdayAt', {time: hourTime});
  }
  return `${calendar} ${translate('common:time.at', {time: hourTime})}`;
};

export const formatEngineTypeValue = (input: string) => {
  const engineTypeOptions = translate('common:editors.engineType.options', {returnObjects: true});
  const engineTypeLabel = engineTypeOptions?.find?.(option => option.value === input)?.label;
  return engineTypeLabel;
};

/**
 * This utility helps to modify the time format to (hh:mm:ss A).
 *
 *  ```typescript
 *
 *  const timeUI  = userPreferredTimeFormatter('1655203587000'); //  12:46:27 AM
 *  const timeUI2 = userPreferredTimeFormatter('1655203852000'); //  12:50:52 AM
 *
 *  ```
 */
export const userPreferredTimeFormatter = (date: DateType, includeSeconds = true): string | DateType => {
  if (date) {
    let timeFormat = UserPreferences.getSystemTimeFormat();
    if (includeSeconds) {
      timeFormat = timeFormat.replace('mm', 'mm:ss');
    }
    return timeFormatter(date, timeFormat);
  }
  return date;
};

/**
 * This utility helps to parse the dates and compare those based on the user
 * prefered date and time format.
 *
 *  ```typescript
 *
 *  const result1 = clientGridDateTimeComparator('12/01/2023', '13/01/2023', 'DD/MM/YYYY'); // 1
 *  const result2 = clientGridDateTimeComparator('12/01/2023', '12/01/2023', 'DD/MM/YYYY'); // 0
 *  const result3 = clientGridDateTimeComparator('13/01/2023', '12/01/2023', 'DD/MM/YYYY'); // -1
 *
 *  ```
 */
export const clientGridDateTimeComparator = (valueA: string, valueB: string) => {
  const dateFormat     = UserPreferences.getSystemDateFormat();
  const timeFormat     = UserPreferences.getSystemTimeFormat().replace('mm', 'mm:ss');
  const dateTimeFormat = `${dateFormat}, ${timeFormat}`;
  return dateTimeSortComparator(valueA, valueB, dateTimeFormat);
};

/**
 * This utility helps create the props for viewing the View SubGroups/SubAccount control.
 *
 * ```typescript
 *
 *  import {getViewSubGroupsProps} from '@ion/dls/src/utils/ComponentUtils';
 *
 *  <DevicesGrid ref={gridRef}
 *    ...
 *    {...getViewSubGroupsProps({
 *      thisPosition : ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS,
 *      viewSubGroupsPosition,
 *      viewSubGroups,
 *      toggleViewSubGroups
 *    })} />
 *
 * ```
 */
export const getViewSubGroupsProps = (
  viewSubGroupsPosition: ViewSubGroupsPosition,
  viewSubGroups: boolean,
  toggleViewSubGroups: Function
): GenericObject => {
  return Object.keys(ViewSubGroupsPosition).reduce((acc, position) => ({
    ...acc,
    [position] : {
      hideSubGroups : viewSubGroupsPosition !== position,
      viewSubGroups,
      toggleViewSubGroups
    }
  }), {});
};

export const getViewOnlyThisAccountProps = (
  viewSubGroupsPosition: ViewSubGroupsPosition,
  viewOnlyThisAccount: boolean,
  toggleViewOnlyThisAccount: Function
): GenericObject => {
  return Object.keys(ViewSubGroupsPosition).reduce((account, position) => ({
    ...account,
    [position] : {
      hideSubGroups : viewSubGroupsPosition !== position,
      viewOnlyThisAccount,
      toggleViewOnlyThisAccount
    }
  }), {});
};

export const getViewInheritableGroupProps = (
  viewSubGroupsPosition: ViewSubGroupsPosition,
  viewInheritableGroup: boolean,
  toggleViewInheritableGroup: Function
): GenericObject => {
  return Object.keys(ViewSubGroupsPosition).reduce((account, position) => ({
    ...account,
    [position] : {
      hideSubGroups : viewSubGroupsPosition !== position,
      viewInheritableGroup,
      toggleViewInheritableGroup
    }
  }), {});
};

export const getViewExcludeInheritedGroupProps = (
  viewSubGroupsPosition: ViewSubGroupsPosition,
  viewExcludeInheritedGroup: boolean,
  toggleViewExcludeInheritedGroup: Function
): GenericObject => {
  return Object.keys(ViewSubGroupsPosition).reduce((account, position) => ({
    ...account,
    [position] : {
      hideSubGroups : viewSubGroupsPosition !== position,
      viewExcludeInheritedGroup,
      toggleViewExcludeInheritedGroup
    }
  }), {});
};

export default resetValue;

/**
 * getTimeSince returns a string representing the time difference between the timestamp and the current time
 *
 * ```typescript
 *
 * import {getTimeSince} from '@ion/core/src/utils/';
 *
 * If the current date is '2023-01-24T01:01:00.000Z' ...
 *
 * getTimeSince('2023-01-24T01:00:00.000Z'); // returns '1 minute ago'
 * getTimeSince('2023-01-24T00:00:00.000Z'); // returns '1 hour ago'
 * getTimeSince('2023-01-23T00:00:00.000Z'); // returns '1 day ago'
 *
 * ```
 * There is also an option to use abbreviated text when using getTimeSince in smaller components.
 * To do so, pass in true as the second parameter.
 *
 * ```typescript
 *
 * import {getTimeSince} from '@ion/core/src/utils/';
 *
 * If the current date is '2023-01-24T01:01:00.000Z' ...
 *
 * getTimeSince('2023-01-24T01:00:00.000Z', true); // returns '1 min ago'
 * getTimeSince('2023-01-24T00:00:00.000Z', true); // returns '1 hr ago'
 * getTimeSince('2023-01-23T00:00:00.000Z', true); // returns '1 day ago'
 *
 * ```
 *
 * @param timestamp the time of the event
 * @returns a string representing the time difference between the timestamp and the current time
 * Taken X minutes/hours/days ago
 */
export const getTimeSince = (timestamp: Date | string, useAbbrevation = false) => {
  if (!isNil(timestamp)) {
    const timeDiff    = Math.abs(new Date().getTime() - new Date(timestamp)?.getTime());
    const diffMinutes = Math.ceil(timeDiff / (1000 * 60));
    const diffHours   = Math.ceil(diffMinutes / 60);
    const diffDays    = Math.ceil(diffHours / 24);
    const abbreviated = useAbbrevation ? 'abbreviation.' : '';
    if (diffMinutes < 60) {
      return translate(`common:time.${abbreviated}minutesFromNow`, {minutesFromNow: diffMinutes});
    }
    if (diffHours === 1) {
      return translate(`common:time.${abbreviated}hourFromNow`, {hourFromNow: diffHours});
    }
    if (diffHours < 24) {
      return translate(`common:time.${abbreviated}hoursFromNow`, {hoursFromNow: diffHours});
    }
    if (diffHours === 24) {
      return translate('common:time.abbreviation.dayFromNow', {daysFromNow: 1});
    }
    if (diffHours > 24) {
      return translate(`common:time.${abbreviated}daysFromNow`, {daysFromNow: diffDays});
    }
    return null;
  }
  return null;
};

/**
 * This utility helps to parse the dates and compare those based on the user
 * prefered date format for grid filter.
 *
 *  ```typescript
 *
 *  const result1 = GridFilterDateComparator('12/01/2023', '13/01/2023', 'DD/MM/YYYY'); // 1
 *  const result2 = GridFilterDateComparator('12/01/2023', '12/01/2023', 'DD/MM/YYYY'); // 0
 *  const result3 = GridFilterDateComparator('01/13/2023', '01/12/2023', 'MM/DD/YYYY'); // -1
 *
 *  ```
 */
export const clientGridDateFilterComparator = (valueA: Date, valueB: string) => (
  clientGridFilterDateComparator(valueA, valueB, UserPreferences.getSystemDateFormat())
);

/**
 * This utility helps to format address string, if any of the state, city, street or zip value is null
 * it displays empty string and then concatenates all values with a comma separator.
 *
 *  ```typescript
 *
 *  const finalAddress = formatAddress({12, California, US, null}); // 12, California, US
 *  const finalAddress = formatAddress({123, null, US, null}); // 12, US
 *
 *  ```
 */
export const formatAddress = (address: AddressType, isGrid?: boolean): string => {
  const {street, street1, city, state, stateProvince, zipcode, zip} = address;
  if (street || street1 || city || state || stateProvince || zip || zipcode) {
    const streetVal = street || street1 || '';
    const stateVal  = state || stateProvince || '';
    const zipVal    = zip || zipcode || '';
    const formatPart = (val: string) => val ? `${val}, ` : '';
    const formatted = `${formatPart(streetVal)}${formatPart(city)}${formatPart(stateVal)}${formatPart(zipVal)}`.trim();
    return formatted.endsWith(',') ? formatted.slice(0, -1) : formatted;
  }
  return !isGrid ? translate('common:notAvailable') : '';
};
